/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import ConsentFieldRenderer from './ConsentFieldRenderer';

// Responsive flex value: full width on mobile (xs), authored span from sm up.
// Keeps Patient Signature (columnSpan=8) usable on small screens — at 390px
// native a /8 cell is too narrow to draw a signature in.
function getFieldWidth(field) {
    const span = field.columnSpan || field.metadata?.style?.columnSpan;
    let desktopWidth;
    if (span) {
        if (span >= 12) {
            desktopWidth = '100%';
        } else {
            const percentage = (span / 12) * 100;
            desktopWidth = `calc(${percentage}% - ${(1 - span / 12) * 16}px)`;
        }
    } else {
        desktopWidth = '100%';
    }
    return { xs: '0 0 100%', sm: `0 0 ${desktopWidth}` };
}

const ConsentDeclaration = ({ fields, fieldValues, onChange, errors, readOnly, theme, forPdf }) => {
    const sortedFields = [...fields].sort((a, b) => a.order - b.order);
    const primaryColor = theme?.primaryColor || '#00AB55';

    return (
        <Box sx={{
            border: `1.5px solid ${primaryColor}30`,
            borderRadius: '12px',
            bgcolor: `${primaryColor}08`,
            overflow: 'hidden',
            mb: 1,
        }}>
            {/* Card header with shield icon */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${primaryColor}20`,
                bgcolor: `${primaryColor}12`,
            }}>
                {/* Shield / verification icon */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 3L4 7v5c0 4.42 3.4 8.57 8 9.54C16.6 20.57 20 16.42 20 12V7L12 3z"
                        fill={primaryColor}
                        opacity="0.15"
                    />
                    <path
                        d="M12 3L4 7v5c0 4.42 3.4 8.57 8 9.54C16.6 20.57 20 16.42 20 12V7L12 3z"
                        stroke={primaryColor}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                    />
                    <polyline
                        points="9 12 11 14 15 10"
                        stroke={primaryColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                <Typography sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#212B36',
                    fontFamily: theme?.fontFamily || 'inherit',
                    letterSpacing: '-0.01em',
                }}>
                    Declaration
                </Typography>
            </Box>

            {/* Fields */}
            <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {sortedFields.map((field) => (
                        <Box key={field.id} data-pdf-block="field" sx={{ flex: getFieldWidth(field), minWidth: 0 }}>
                            <ConsentFieldRenderer
                                field={field}
                                value={fieldValues[field.id]}
                                onChange={onChange}
                                error={errors[field.id]}
                                readOnly={readOnly}
                                theme={theme}
                                forPdf={forPdf}
                            />
                        </Box>
                    ))}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                    Date: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </Typography>
            </Box>
        </Box>
    );
};

ConsentDeclaration.propTypes = {
    fields: PropTypes.array.isRequired,
    fieldValues: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.object,
    readOnly: PropTypes.bool,
    theme: PropTypes.object,
    forPdf: PropTypes.bool,
};

ConsentDeclaration.defaultProps = {
    errors: {},
    readOnly: false,
    theme: null,
    forPdf: false,
};

export default ConsentDeclaration;
